import { useQuery } from '@tanstack/react-query';
import type { ProductDocument } from '@util/types/firestore/products';
import {
  effectivePrice,
  type EffectivePriceResult,
  type StoreSaleCurrent,
} from '@util/storeSale/effectivePrice';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface PublicStoreSale {
  seller_id: string;
  sale_id: string;
  percent_off: number;
  starts_at: string;
  ends_at: string;
}

interface StoreSaleContextValue {
  sales: PublicStoreSale[];
  ready: boolean;
  getSaleForSeller: (sellerId: string) => PublicStoreSale | null;
  getEffectivePriceForProduct: (
    product: Pick<ProductDocument, 'price' | 'seller_id' | 'on_sale' | 'is_auction'> | null | undefined,
    serverSale?: StoreSaleCurrent | null
  ) => EffectivePriceResult | null;
}

// Isolated pages/tests can render without _app. In that case they show list
// prices; the server validator still decides the charge.
const noProviderValue: StoreSaleContextValue = {
  sales: [], ready: true,
  getSaleForSeller: () => null,
  getEffectivePriceForProduct: (product) => product
    ? effectivePrice(product, null, Date.now(), { enabled: false }) : null,
};
const StoreSaleContext = createContext<StoreSaleContextValue>(noProviderValue);

export function StoreSaleProvider({
  children,
  initialSales = [],
  initialNowMs,
}: {
  children: React.ReactNode;
  initialSales?: PublicStoreSale[];
  initialNowMs?: number;
}) {
  const [nowMs, setNowMs] = useState(() => initialNowMs ?? Date.now());
  useEffect(() => setNowMs(Date.now()), []);
  const query = useQuery<{ enabled?: boolean; sales: PublicStoreSale[] }>({
    queryKey: ['store-sales-active'],
    queryFn: async () => {
      const response = await fetch('/api/store-sales/active');
      if (!response.ok) throw new Error('Store sales unavailable');
      return response.json();
    },
    staleTime: 15_000,
    // Re-check cheaply while disabled so a focused tab notices the switch
    // turning on without needing a reload or a focus change.
    refetchInterval: (data) => data?.enabled === false ? 300_000 : 15_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  // Keep the last successful public list during a transient failed poll.
  // Checkout independently validates the current server price.
  const sales = query.data?.sales ?? initialSales;
  const nextBoundary = useMemo(() => sales
    .flatMap((sale) => [Date.parse(sale.starts_at), Date.parse(sale.ends_at)])
    .filter((boundary) => Number.isFinite(boundary) && boundary > nowMs)
    .sort((a, b) => a - b)[0], [sales, nowMs]);
  useEffect(() => {
    if (!nextBoundary) return;
    const timeout = window.setTimeout(() => setNowMs(Date.now()), Math.min(nextBoundary - Date.now() + 1, 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [nextBoundary]);

  const getSaleForSeller = useCallback((sellerId: string) =>
    sales.find((sale) => sale.seller_id === sellerId) ?? null, [sales]);
  const getEffectivePriceForProduct = useCallback((
    product: Pick<ProductDocument, 'price' | 'seller_id' | 'on_sale' | 'is_auction'> | null | undefined,
    serverSale?: StoreSaleCurrent | null
  ): EffectivePriceResult | null => {
    if (!product) return null;
    // SSR sale is for the first paint only. Once the live public read settles,
    // it wins so a cancellation or changed sale cannot stay pinned forever.
    const sale = query.isLoading && serverSale !== undefined
      ? serverSale : getSaleForSeller(product.seller_id);
    return effectivePrice(product, sale, nowMs, { enabled: true, allowlist: null });
  }, [getSaleForSeller, nowMs, query.isLoading]);
  // A failed first read is not evidence that no sale exists. Keep cart lines
  // untouched until a successful public list (including a genuine empty list)
  // arrives; a later failed poll can still use its last successful data.
  const ready = query.data !== undefined;
  const value = useMemo(() => ({ sales, ready, getSaleForSeller, getEffectivePriceForProduct }),
    [sales, ready, getSaleForSeller, getEffectivePriceForProduct]);
  return <StoreSaleContext.Provider value={value}>{children}</StoreSaleContext.Provider>;
}

export function useStoreSales(): StoreSaleContextValue {
  return useContext(StoreSaleContext);
}

export function useEffectivePrice(
  product: Pick<ProductDocument, 'price' | 'seller_id' | 'on_sale' | 'is_auction'> | null | undefined,
  serverSale?: StoreSaleCurrent | null
): EffectivePriceResult | null {
  return useStoreSales().getEffectivePriceForProduct(product, serverSale);
}
